import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../contexts/AuthContext";
import { useConfirm } from "../../../hooks/useConfirm";
import {
  getCaseDetails,
  updateCaseStatus,
  getCaseDocuments,
  getCaseEvents,
  addCaseNote,
} from "@/shared/db";
import { EditCaseModal } from "../components/EditCaseModal";
import { AssignDocumentToCaseModal } from "../components/AssignDocumentToCaseModal";
import {
  ArrowLeftIcon,
  DocumentTextIcon,
  ClockIcon,
  UserIcon,
  CalendarIcon,
  PencilIcon,
  CheckCircleIcon,
  BriefcaseIcon,
  PlusIcon,
} from "@heroicons/react/24/outline";
import { toast } from "sonner";
import { Button, Select, Textarea } from "@/shared/ui";
import { DetailPageSkeleton } from "../../../components/skeletons";
import {
  formatDateFull,
  generateCaseCalendar,
  downloadCalendarFile,
} from "@/shared/core";
import type {
  CaseWithDetails,
  CaseDocument,
  CaseEvent,
} from "../types/case.types";

export const CaseDetailsPage: React.FC = () => {
  const { caseId } = useParams<{ caseId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const { confirm, Modal } = useConfirm();

  const [caseData, setCaseData] = useState<CaseWithDetails | null>(null);
  const [documents, setDocuments] = useState<CaseDocument[]>([]);
  const [events, setEvents] = useState<CaseEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [noteText, setNoteText] = useState("");
  const [addingNote, setAddingNote] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isAssignDocumentModalOpen, setIsAssignDocumentModalOpen] =
    useState(false);

  useEffect(() => {
    if (caseId && user) {
      loadCaseData();
    }
  }, [caseId, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCaseData = async () => {
    if (!caseId || !user) return;

    try {
      setLoading(true);

      const [caseDetails, caseDocuments, caseEvents] = await Promise.all([
        getCaseDetails(caseId),
        getCaseDocuments(caseId),
        getCaseEvents(caseId),
      ]);

      // Handle profiles being null or array
      if (caseDetails) {
        const caseDetailsData = caseDetails as unknown as Record<
          string,
          unknown
        >;
        const clientProfiles = caseDetailsData.client_profiles as
          | {
              profiles?:
                | {
                    id: string;
                    full_name: string;
                    email: string;
                    country: string;
                    profile_image_url?: string;
                  }
                | Array<{
                    id: string;
                    full_name: string;
                    email: string;
                    country: string;
                    profile_image_url?: string;
                  }>
                | null;
            }
          | undefined;

        if (clientProfiles && Array.isArray(clientProfiles.profiles)) {
          clientProfiles.profiles = clientProfiles.profiles[0];
        }
      }

      setCaseData(caseDetails as unknown as CaseWithDetails);
      setDocuments(caseDocuments as unknown as CaseDocument[]);
      setEvents(caseEvents as unknown as CaseEvent[]);
    } catch (error) {
      console.error("Error loading case data:", error);
      toast.error(t("lawyer.caseDetails.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus: string) => {
    if (!caseId || !caseData) return;

    await confirm({
      title: t("lawyer.caseDetails.changeStatus.title"),
      message: t("lawyer.caseDetails.changeStatus.message", {
        status: t(`lawyer.cases.status.${newStatus}`),
      }),
      confirmText: t("common.update"),
      cancelText: t("common.cancel"),
      variant: "warning",
      onConfirm: async () => {
        try {
          await updateCaseStatus(
            caseId,
            newStatus as "pending" | "active" | "closed" | "archived",
            user?.id
          );
          toast.success(t("lawyer.caseDetails.changeStatus.success"));
          await loadCaseData();
        } catch (error) {
          console.error("Error updating case status:", error);
          toast.error(t("lawyer.caseDetails.changeStatus.error"));
        }
      },
    });
  };

  const handleAddNote = async () => {
    if (!caseId || !user || !noteText.trim()) return;

    try {
      setAddingNote(true);
      await addCaseNote(caseId, {
        content: noteText,
        author_id: user.id,
        author_name: user.email || "Lawyer",
      });

      setNoteText("");
      toast.success(t("lawyer.caseDetails.noteAdded"));
      await loadCaseData();
    } catch (error) {
      console.error("Error adding note:", error);
      toast.error(t("lawyer.caseDetails.noteError"));
    } finally {
      setAddingNote(false);
    }
  };

  const getStatusBadge = (status?: string) => {
    if (!status) return null;

    switch (status) {
      case "pending":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-atlantis-100 text-atlantis-900">
            {t("lawyer.cases.status.pending")}
          </span>
        );
      case "active":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-atlantis-100 text-atlantis-900">
            <CheckCircleIcon className="w-4 h-4 mr-1" />
            {t("lawyer.cases.status.active")}
          </span>
        );
      case "closed":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-tiber-100 text-tiber-800">
            {t("lawyer.cases.status.closed")}
          </span>
        );
      case "archived":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-tiber-500/10 text-tiber-600">
            {t("lawyer.cases.status.archived")}
          </span>
        );
      default:
        return null;
    }
  };

  const getPriorityBadge = (priority?: string) => {
    if (!priority) return null;

    switch (priority) {
      case "urgent":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-900">
            {t("lawyer.cases.priority.urgent")}
          </span>
        );
      case "high":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-tiber-100 text-tiber-900">
            {t("lawyer.cases.priority.high")}
          </span>
        );
      case "medium":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-tiber-500/10 text-tiber-600">
            {t("lawyer.cases.priority.medium")}
          </span>
        );
      case "low":
        return (
          <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-tiber-100 text-tiber-800">
            {t("lawyer.cases.priority.low")}
          </span>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <DetailPageSkeleton className="bg-linear-to-br from-romance-50 via-white to-romance-100" />
    );
  }

  if (!caseData) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-linear-to-br from-romance-50 via-white to-romance-100">
        <div className="text-center bg-white rounded-xl shadow-lg p-12">
          <p className="text-tiber-700 mb-4">
            {t("lawyer.caseDetails.notFound")}
          </p>
          <button
            onClick={() => navigate("/dashboard/lawyer/cases")}
            className="px-6 py-3 bg-linear-to-r from-tiber-500 to-tiber-600 text-white rounded-lg hover:from-tiber-600 hover:to-tiber-700 transition-all shadow-md hover:shadow-lg font-medium"
          >
            {t("lawyer.caseDetails.back")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      <div className="mx-auto">
        {/* Header */}
        <Button
          onClick={() => navigate("/dashboard/lawyer/cases")}
          variant="ghost"
          className="flex items-center text-tiber-600 hover:text-tiber-900 mb-6 font-medium transition-colors"
          leftIcon={<ArrowLeftIcon className="h-5 w-5 mr-2" />}
        >
          {t("lawyer.caseDetails.back")}
        </Button>

        {/* Case Info Card */}
        <div className="bg-white rounded-xl shadow-lg border border-tiber-600 p-8 mb-6">
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-start space-x-6 flex-1">
              {/* Icon */}
              <div>
                <div className="h-28 w-28 rounded-2xl bg-linear-to-br from-atlantis-500 to-atlantis-700 flex items-center justify-center border-4 border-tiber-100">
                  <BriefcaseIcon className="h-14 w-14 text-white" />
                </div>
              </div>

              {/* Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-3xl font-bold text-tiber-900 mb-2">
                      {caseData.title}
                    </h1>
                    <p className="text-sm font-mono text-tiber-600 mb-3">
                      {caseData.case_number}
                    </p>
                    <div className="flex items-center gap-2 flex-wrap">
                      {getStatusBadge(caseData.status)}
                      {getPriorityBadge(caseData.priority)}
                      <span className="inline-flex items-center px-3 py-1 text-sm font-medium bg-tiber-100 text-tiber-700 rounded-full capitalize">
                        {t(
                          `lawyer.cases.practiceAreas.${caseData.practice_area}`,
                          caseData.practice_area
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                {caseData.description && (
                  <div className="mb-4">
                    <p className="text-sm text-tiber-600">
                      {caseData.description}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center space-x-2 text-sm text-tiber-600">
                    <UserIcon className="h-5 w-5" />
                    <span>
                      {caseData.client_profiles?.profiles?.full_name || "N/A"}
                    </span>
                  </div>
                  {caseData.client_profiles?.profiles?.email && (
                    <div className="flex items-center space-x-2 text-sm text-tiber-600">
                      <DocumentTextIcon className="h-5 w-5" />
                      <span>{caseData.client_profiles.profiles.email}</span>
                    </div>
                  )}
                  <div className="flex items-center space-x-2 text-sm text-tiber-600">
                    <CalendarIcon className="h-5 w-5" />
                    <span>{formatDateFull(caseData.created_at)}</span>
                  </div>
                  {caseData.started_at && (
                    <div className="flex items-center space-x-2 text-sm text-tiber-600">
                      <ClockIcon className="h-5 w-5" />
                      <span>
                        {t("lawyer.caseDetails.startedAt")}:{" "}
                        {formatDateFull(caseData.started_at)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col gap-2 ml-6">
              <Button
                onClick={() => setIsEditModalOpen(true)}
                variant="primary"
                leftIcon={<PencilIcon className="h-5 w-5" />}
              >
                {t("lawyer.caseDetails.edit")}
              </Button>
              <Select
                options={[
                  {
                    value: "pending",
                    label: t("lawyer.cases.status.pending"),
                  },
                  {
                    value: "active",
                    label: t("lawyer.cases.status.active"),
                  },
                  {
                    value: "closed",
                    label: t("lawyer.cases.status.closed"),
                  },
                  {
                    value: "archived",
                    label: t("lawyer.cases.status.archived"),
                  },
                ]}
                value={caseData.status}
                onChange={(e) => handleStatusChange(e.target.value as string)}
              />
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Documents */}
            <div className="bg-white rounded-xl shadow-lg border border-tiber-600 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-juridico flex items-center">
                  <DocumentTextIcon className="h-5 w-5 mr-2" />
                  {t("lawyer.caseDetails.documents")} ({documents.length})
                </h2>
                <button
                  onClick={() => setIsAssignDocumentModalOpen(true)}
                  className="px-4 py-2 bg-atlantis-600 text-white rounded-lg hover:bg-atlantis-700 transition-colors text-sm font-medium"
                >
                  {t("lawyer.caseDetails.assignDocument")}
                </button>
              </div>
              {documents.length > 0 ? (
                <div className="space-y-2">
                  {documents.map((doc) => (
                    <div
                      key={doc.id}
                      className="flex items-center justify-between p-3 bg-romance-50 rounded-lg hover:bg-tiber-100 transition-colors"
                    >
                      <div>
                        <p className="font-medium text-juridico">
                          {doc.documents?.title || "N/A"}
                        </p>
                        <p className="text-sm text-titan">
                          {formatDateFull(doc.created_at)}
                        </p>
                      </div>
                      <button
                        onClick={() =>
                          navigate(`/dashboard/document/${doc.documents?.id}`)
                        }
                        className="text-atlantis-600 hover:text-atlantis-700 text-sm font-medium"
                      >
                        {t("lawyer.caseDetails.view")}
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-titan text-sm">
                  {t(
                    "lawyer.caseDetails.noDocuments",
                    "No documents assigned to this case"
                  )}
                </p>
              )}
            </div>

            {/* Timeline / Events */}
            <div className="bg-white rounded-xl shadow-lg border border-tiber-600 p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-bold text-juridico flex items-center">
                  <ClockIcon className="h-5 w-5 mr-2" />
                  {t("lawyer.caseDetails.timeline")}
                </h2>
                {events.length > 0 && (
                  <Button
                    variant="secondary"
                    onClick={() => {
                      try {
                        const calendarContent = generateCaseCalendar({
                          caseId: caseId || "",
                          caseTitle: caseData.title,
                          events: events,
                          country: caseData.country,
                        });
                        downloadCalendarFile(
                          calendarContent,
                          `lawgentia-caso-${caseId}.ics`
                        );
                        toast.success(t("lawyer.caseDetails.calendarExported"));
                      } catch (error) {
                        console.error("Error exporting calendar:", error);
                        toast.error(t("lawyer.caseDetails.calendarError"));
                      }
                    }}
                    leftIcon={<CalendarIcon className="h-5 w-5" />}
                  >
                    {t("lawyer.caseDetails.exportCalendar")}
                  </Button>
                )}
              </div>
              {events.length > 0 ? (
                <div className="space-y-4">
                  {events.map((event, index) => (
                    <div key={event.id} className="flex space-x-3">
                      <div className="shrink-0">
                        <div className="h-8 w-8 rounded-full bg-atlantis-100 flex items-center justify-center">
                          <div className="h-2 w-2 rounded-full bg-atlantis-600"></div>
                        </div>
                        {index < events.length - 1 && (
                          <div className="h-full w-px bg-tiber-200 mx-auto mt-2"></div>
                        )}
                      </div>
                      <div className="flex-1 pb-4">
                        <p className="font-medium text-juridico">
                          {event.event_title}
                        </p>
                        {event.event_description && (
                          <p className="text-sm text-tiber-700">
                            {event.event_description}
                          </p>
                        )}
                        <p className="text-xs text-titan mt-1">
                          {formatDateFull(event.event_date)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-titan text-sm">
                  {t("lawyer.caseDetails.noEvents")}
                </p>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Add Note */}
            <div className="bg-white rounded-xl shadow-lg border border-tiber-600 p-6">
              <h3 className="text-lg font-bold text-juridico mb-4 flex items-center">
                <PencilIcon className="h-5 w-5 mr-2" />
                {t("lawyer.caseDetails.addNote")}
              </h3>
              <Textarea
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
                placeholder={t(
                  "lawyer.caseDetails.notePlaceholder",
                  "Write a note about this case..."
                )}
                rows={6}
              />
              <Button
                onClick={handleAddNote}
                disabled={!noteText.trim() || addingNote}
                variant="primary"
                leftIcon={<PlusIcon className="h-5 w-5 mr-2" />}
                className="w-full mt-4"
              >
                {t("lawyer.caseDetails.addNoteButton")}
              </Button>
            </div>

            {/* Notes List */}
            {caseData.notes && caseData.notes.length > 0 && (
              <div className="bg-white rounded-xl shadow-lg border border-tiber-600 p-6">
                <h3 className="text-lg font-bold text-juridico mb-4">
                  {t("lawyer.caseDetails.notes")}
                </h3>
                <div className="space-y-3">
                  {caseData.notes.map((note, index: number) => (
                    <div key={index} className="bg-romance-50 p-3 rounded-lg">
                      <p className="text-sm text-juridico mb-1">
                        {note.content}
                      </p>
                      <p className="text-xs text-titan">
                        {note.author_name} • {formatDateFull(note.timestamp)}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Client Info */}
            {caseData.client_profiles?.profiles && (
              <div className="bg-white rounded-xl shadow-lg border border-tiber-600 p-6">
                <h3 className="text-lg font-bold text-juridico mb-4">
                  {t("lawyer.caseDetails.clientInfo")}
                </h3>
                <div className="space-y-2 text-sm">
                  <div>
                    <p className="text-titan">
                      {t("lawyer.caseDetails.clientName")}
                    </p>
                    <p className="font-medium text-juridico">
                      {caseData.client_profiles.profiles.full_name || "N/A"}
                    </p>
                  </div>
                  <div>
                    <p className="text-titan">
                      {t("lawyer.caseDetails.clientEmail")}
                    </p>
                    <p className="font-medium text-juridico">
                      {caseData.client_profiles.profiles.email || "N/A"}
                    </p>
                  </div>
                  {caseData.client_profiles.phone && (
                    <div>
                      <p className="text-titan">
                        {t("lawyer.caseDetails.clientPhone")}
                      </p>
                      <p className="font-medium text-juridico">
                        {caseData.client_profiles.phone}
                      </p>
                    </div>
                  )}
                  <Button
                    onClick={() =>
                      navigate(
                        `/dashboard/lawyer/clients/${caseData.client_id}`
                      )
                    }
                  >
                    {t("lawyer.caseDetails.viewClient")}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Modals */}
        <EditCaseModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSuccess={loadCaseData}
          case_={caseData}
        />

        <AssignDocumentToCaseModal
          isOpen={isAssignDocumentModalOpen}
          onClose={() => setIsAssignDocumentModalOpen(false)}
          onSuccess={loadCaseData}
          caseId={caseData.id}
          caseTitle={caseData.title}
          userId={user?.id || ""}
        />

        {Modal}
      </div>
    </div>
  );
};
